import { useLocale } from '@mirai/locale';
import { Progress, styles, Text, View } from '@mirai/ui';
import PropTypes from 'prop-types';
import React, { useEffect, useId, useRef, useState } from 'react';

import { L10N } from './FormRedsys.l10n';
import * as style from './FormRedsys.module.css';
import { Form } from '../Form';

export const FormRedsys = ({ response }) => {
  const { translate } = useLocale();
  const frameName = useId();
  const [redirectUrl, setRedirectUrl] = useState();
  const [loaded, setLoaded] = useState();

  const iframe = useRef();
  const completed = useRef(false);

  useEffect(() => {
    const serviceOrigin = new URL(process.env.SERVICE_BOOKING).origin;
    const handleMessage = ({ origin, source, data }) => {
      const { type, url } = data || {};
      if (
        completed.current ||
        origin !== serviceOrigin ||
        source !== iframe.current?.contentWindow ||
        type !== 'mirai:payment-return-frame' ||
        typeof url !== 'string'
      )
        return;

      let destination;
      try {
        destination = new URL(url);
      } catch {
        return;
      }
      if (
        destination.protocol !== 'https:' ||
        destination.username ||
        destination.password ||
        ![serviceOrigin, window.location.origin].includes(destination.origin)
      )
        return;

      completed.current = true;
      window.removeEventListener('message', handleMessage);
      setRedirectUrl(destination.href);
    };
    window.addEventListener('message', handleMessage);

    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    if (redirectUrl) window.location.href = redirectUrl;
  }, [redirectUrl]);

  const { configuration } = response;

  return (
    <View className={style.container}>
      <Progress indeterminate visible={!loaded} role="progressbar" aria-label={translate(L10N.LABEL_REDIRECTING)} />
      {redirectUrl ? (
        <View
          className={style.redirecting}
          role="status"
          aria-label={translate(L10N.LABEL_REDIRECTING)}
          testId="redsys-redirecting"
        >
          <Text>{translate(L10N.LABEL_REDIRECTING)}</Text>
          <Progress indeterminate visible role="progressbar" aria-label={translate(L10N.LABEL_REDIRECTING)} />
        </View>
      ) : (
        <View
          ref={iframe}
          tag="iframe"
          name={frameName}
          allow="payment *"
          onLoad={() => setLoaded(true)}
          className={styles(style.iframe, !loaded && style.hide)}
        />
      )}
      <Form {...configuration} target={frameName} />
    </View>
  );
};

FormRedsys.displayName = 'Mirai:Core:ButtonPayment:FormRedsys';

FormRedsys.propTypes = {
  response: PropTypes.shape({
    configuration: PropTypes.shape({
      actionUrl: PropTypes.string,
      formFields: PropTypes.array,
      method: PropTypes.string,
    }),
  }).isRequired,
};
